import React, { useMemo } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";

const sxPropType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.string, PropTypes.shape({})])
  ),
  PropTypes.shape({}),
]);

function ColorPaletteGrid({
  colors,
  selectedColor,
  onSelect,
  columns = 5,
  size = 32,
  gap = 1,
  selectedBorderColor = "#000",
  selectedBorderWidth = 3,
  unselectedBorderColor = "#e0e0e0",
  unselectedBorderWidth = 2,
  disabledColors = [],
  sx = {},
}) {
  const disabledSet = useMemo(
    () => new Set(disabledColors.filter((color) => !!color)),
    [disabledColors]
  );

  return (
    <MDBox
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(${size}px, 1fr))`,
        gap,
        ...sx,
      }}
    >
      {colors.map((color) => {
        const isSelected = selectedColor === color;
        const isDisabled = disabledSet.has(color) && !isSelected;

        return (
          <MDBox
            key={color}
            role="button"
            tabIndex={0}
            aria-label={`Select color ${color}`}
            sx={{
              width: size,
              height: size,
              backgroundColor: color,
              borderRadius: "4px",
              cursor: isDisabled ? "not-allowed" : "pointer",
              border: isSelected
                ? `${selectedBorderWidth}px solid ${selectedBorderColor}`
                : `${unselectedBorderWidth}px solid ${unselectedBorderColor}`,
              opacity: isDisabled ? 0.4 : 1,
              pointerEvents: isDisabled ? "none" : "auto",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: isDisabled ? "none" : "scale(1.05)",
                boxShadow: isDisabled ? "none" : "0 2px 8px rgba(0,0,0,0.2)",
              },
            }}
            onClick={() => !isDisabled && onSelect(color)}
            onKeyDown={(event) => {
              if (!isDisabled && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onSelect(color);
              }
            }}
          />
        );
      })}
    </MDBox>
  );
}

ColorPaletteGrid.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedColor: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  columns: PropTypes.number,
  size: PropTypes.number,
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedBorderColor: PropTypes.string,
  selectedBorderWidth: PropTypes.number,
  unselectedBorderColor: PropTypes.string,
  unselectedBorderWidth: PropTypes.number,
  disabledColors: PropTypes.arrayOf(PropTypes.string),
  sx: sxPropType,
};

ColorPaletteGrid.defaultProps = {
  selectedColor: "",
  columns: 5,
  size: 32,
  gap: 1,
  selectedBorderColor: "#000",
  selectedBorderWidth: 3,
  unselectedBorderColor: "#e0e0e0",
  unselectedBorderWidth: 2,
  disabledColors: [],
  sx: {},
};

export default React.memo(ColorPaletteGrid);
